// The printed sheet: floor-plan placement guide, then the signs themselves.
//
// Sizes are in mm against a real page rather than px, because this is the one
// output whose result is a physical object.
//
// Ported from UMapsTools' QrCodeDirectory print sheet — the two produce the same
// artefact, and a sign printed from either must be indistinguishable on a wall.
import { planSvg } from './signPlanSvg.js';
import { signUrl, floorTag, prettifySlug } from './signs.js';
import { BADGE, MARK_CSS_WIDTH, UMAPS_BADGE_URL, qrBadgeSvg, qrBadgeSvgEmbedded } from './qrBadge.js';

// The badge geometry and the mark now live in utils/qrBadge.js, shared with the
// Event Map Creator's share step and the public event page's QR popup — a
// printed sign and an event poster carry the same mark in the same place, and
// two copies of the fractions is how that stops being true. Re-exported under
// the names this module has always used so its callers did not have to move.
export { BADGE };
export const LOGO_URL = UMAPS_BADGE_URL;
export const signQrSvg = qrBadgeSvg;

const esc = s => String(s == null ? '' : s).replace(/[<>&]/g, ch => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[ch]));

/**
 * Build the whole sheet as an HTML document string.
 *
 * @param {object}  o
 * @param {string}  o.slug
 * @param {string}  o.building  the building's display name (defaults to the slug,
 *                              prettified) — printed on a room sign in place of
 *                              the identifier
 * @param {Array}   o.signs     the ENABLED signs, in the order they will print
 * @param {Array}   o.floors    [{ floorNum, plan }] — plan from planFloor()
 * @param {string}  o.base      the link base ("https://myumaps.ca/umanitoba")
 * @param {number}  o.perPage   1 (portrait) or 2 (landscape, side by side)
 * @param {string}  o.logoUrl   the badge image, served from the site's own origin
 * @param {string}  o.logoDataUrl the mark as a data: URI, from
 *                              `loadBadgeDataUrl()`. ⚠️ Pass it. See `markInto`.
 */
export function buildSignSheet({
  slug, building, signs, floors, base, perPage = 1,
  logoUrl = LOGO_URL, logoDataUrl = null,
}) {
  const two = perPage === 2;
  const buildingName = building || prettifySlug(slug);

  /**
   * The code with the mark already in it.
   *
   * ⚠️ EMBEDDED, not overlaid, whenever the caller managed to read the mark.
   * This sheet exists to be printed, and an `<img>` laid over the SVG is a
   * second network request racing `window.print()`: lose that race and a
   * hundred cards come off the printer with a white hole in the middle, with
   * nothing logged and nothing on screen to show it happened. The `<img>` is
   * kept only as the fallback for a null data URI, where the alternative is no
   * mark at all.
   */
  const markInto = url => (logoDataUrl
    ? qrBadgeSvgEmbedded(url, 1000, logoDataUrl)
    : `${qrBadgeSvg(url, 1000)}<img class="badge" src="${esc(logoUrl)}" alt="">`);

  /**
   * What goes in the bottom corner of a card.
   *
   * A sign placed at a ROOM says where it is — "Armes, Room 154" — because that
   * is what the person standing in front of it can check against the door, and
   * what they will quote when they report one. A sign in a corridor has no such
   * name, so it keeps the identifier, which is the only thing that distinguishes
   * one junction from the next.
   */
  const identOf = c => (c.location ? `${buildingName}, ${c.location}` : c.code);

  // Card geometry, in mm. Two-up turns the PAGE landscape rather than squeezing
  // two portrait cards onto a portrait page — halving the width would take the
  // QR below a comfortable scanning size, which is the one dimension of this
  // design that isn't negotiable.
  const cardW = two ? 128 : 168;
  const qrW = two ? 88 : 116;

  const card = c => `
    <figure class="card">
      <h2>Find your way<br>around campus!</h2>
      <div class="qr">
        ${markInto(signUrl(c, base))}
      </div>
      <div class="addr">${esc(String(base).replace(/^https?:\/\//, '').replace(/\/+$/, ''))}</div>
      <div class="node">${esc(c.nodeId)}</div>
      <div class="ident${c.location ? ' where' : ''}">${esc(identOf(c))}</div>
    </figure>`;

  // ── Placement guide ──────────────────────────────────────────────────────
  // The sheet leads with a map of where each sign goes, because the person
  // carrying it round the building has to find these spots and they are corridor
  // junctions and entrance walls, not room numbers. Each sign is labelled on the
  // plan with its own identifier; the legend beside it carries the same code and
  // where the sign is posted, which is the sentence somebody reads while looking
  // for the spot.
  const plansPerPage = two ? 3 : 2;

  const withSigns = (floors || [])
    .map(f => ({ ...f, signs: signs.filter(s => s.floor === f.floorNum) }))
    .filter(f => f.signs.length && f.plan);

  const planFig = ({ floorNum, plan, signs: fs }) => {
    const marks = fs.map(c => ({ nodeId: c.nodeId, code: c.code }));
    const legend = fs.map(c => `
      <li><span class="lg-code">${esc(c.code)}</span>${
        c.location ? ` <span class="lg-where">${esc(c.location)}</span>` : ''}</li>`).join('');
    // A wide floor takes the full page width with its legend underneath; a
    // squarish one puts the legend beside it. Portrait only — a landscape column
    // is too narrow for anything to sit next to the plan. The split is real:
    // measured across the snapshot, campus is about half and half.
    const wide = !two && plan.aspect > 1.6;
    const box = two ? { w: 560, h: 700 } : wide ? { w: 940, h: 400 } : { w: 760, h: 720 };
    return `
      <figure class="plan${wide ? ' wide' : ''}">
        <figcaption>Floor ${esc(floorTag(floorNum))} · ${fs.length} sign${fs.length === 1 ? '' : 's'}</figcaption>
        <div class="plan-body">
          <div class="plan-svg">${planSvg(plan, { ...box, marks })}</div>
          <ol class="plan-legend">${legend}</ol>
        </div>
      </figure>`;
  };

  const guideChunks = [];
  for (let i = 0; i < withSigns.length; i += plansPerPage) {
    guideChunks.push(withSigns.slice(i, i + plansPerPage));
  }
  const guidePages = guideChunks.map((group, i) => `
    <section class="page guide">
      <h1 class="guide-h">Where these signs go — ${esc(prettifySlug(slug))}${
        guideChunks.length > 1 ? ` <span class="guide-n">${i + 1} of ${guideChunks.length}</span>` : ''}</h1>
      <div class="guide-grid">${group.map(planFig).join('')}</div>
    </section>`).join('');

  const chunks = [];
  for (let i = 0; i < signs.length; i += perPage) chunks.push(signs.slice(i, i + perPage));
  const cardPages = chunks.map(group => `
    <section class="page">
      ${group.map(card).join('')}
    </section>`).join('');

  return `<!doctype html><meta charset="utf-8"><title>UMaps signs — ${esc(prettifySlug(slug))}</title>
    <style>
      @page { size: ${two ? 'letter landscape' : 'letter portrait'}; margin: 10mm; }
      * { box-sizing: border-box; }
      body { margin: 0; color: #000;
             font: 400 14px/1.3 "Helvetica Neue", Helvetica, Arial, sans-serif; }
      /* One page per sign (or per pair), centred on it.
         BREAK-BEFORE, not break-after. A forced break *after* a box that already
         fills its sheet asks for a new page when the printer has just started
         one, and the browser obliges — a blank sheet between every card. Putting
         the break BEFORE every page except the first makes exactly one break
         between neighbours and none at either end, so the rule cannot produce a
         page with nothing on it. (The old .page.last exception existed to undo
         half of this problem; break-before removes the need for it.)

         The height is also deliberately WELL under the printable area rather
         than equal to it. The @page margin is advisory: most dialogs offer their
         own and default to 0.5in, which leaves 254mm on Letter — so a box sized
         against this file's 10mm margins (259mm) is taller than the sheet it
         lands on and spills. 240/174 is measured to fit Letter and A4, portrait
         and landscape, at 10mm, 0.5in and 1in — every browser default and the
         one a person is most likely to set by hand.
         overflow:hidden is the belt to those braces. */
      .page {
        height: ${two ? 174 : 240}mm;
        overflow: hidden;
        display: flex; align-items: center; justify-content: center; gap: 8mm;
        break-inside: avoid; page-break-inside: avoid;
      }
      .page + .page { break-before: page; page-break-before: always; }
      .card {
        width: ${cardW}mm; margin: 0; position: relative;
        padding: ${two ? 8 : 10}mm ${two ? 6 : 8}mm ${two ? 7 : 9}mm;
        border: 1.5px solid #0060a8;      /* UM Blue: a cut line, and the only colour here */
        border-radius: 4mm; text-align: center;
        break-inside: avoid; page-break-inside: avoid;
      }
      h2 { margin: 0 0 ${two ? 7 : 9}mm; font-size: ${two ? 26 : 34}pt; font-weight: 800;
           line-height: 1.08; letter-spacing: -0.01em; }
      /* The QR and its badge share one positioning context so the badge is
         centred on the SYMBOL, not on the card — the two differ once the SVG's
         own quiet zone is in play. Only the fallback overlay needs this; the
         embedded mark is placed in the SVG's own coordinates and cannot be
         off-centre. */
      .qr { position: relative; width: ${qrW}mm; margin: 0 auto; line-height: 0; }
      .qr svg { width: 100%; height: auto; display: block; }
      /* Width only, height AUTO: the logo file is square, so its own intrinsic
         ratio makes the box square and it cannot be squashed. Sizing a wrapper
         with aspect-ratio and stretching the image to fill it renders as a
         vertical oval — an aspect-ratio box holding a height:100% child is a
         circular definition the engine may resolve either way. The percentage
         comes from qrBadge.js, so it is the same mark the app draws. */
      .badge {
        position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
        width: ${MARK_CSS_WIDTH}; height: auto; display: block;
      }
      .addr { margin-top: ${two ? 6 : 8}mm; font-size: ${two ? 17 : 22}pt; font-weight: 800; }
      /* The node id the QR actually carries — so a sign can be checked, or typed
         by hand, without decoding the symbol. In the corner OPPOSITE the
         identifier and smaller than it: both are for us, and the card belongs to
         the invitation and the address. */
      .node {
        position: absolute; left: ${two ? 4 : 5}mm; bottom: ${two ? 3 : 4}mm;
        max-width: 58%; text-align: left;
        font-size: ${two ? 5 : 6}pt; line-height: 1.2;
        font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
        color: #cfcfcf; word-break: break-all;
      }
      /* Nearly invisible, and out of the way in the corner. The identifier is
         for US — the only thing that names a sign when somebody reports one —
         and a student reading the poster should see the invitation and the
         address, not a serial number. */
      .ident {
        position: absolute; right: ${two ? 4 : 5}mm; bottom: ${two ? 3 : 4}mm;
        font-size: ${two ? 9 : 11}pt; font-weight: 700;
        letter-spacing: 0.14em; color: #dcdcdc;
      }
      /* A room's name is words, not a serial: it needs several times the room a
         code does, so it drops the tracking and comes down a size rather than
         running under the node id in the opposite corner. */
      .ident.where {
        letter-spacing: 0.02em; font-size: ${two ? 7 : 8.5}pt;
        max-width: 60%; text-align: right;
      }

      /* ── Placement guide pages ─────────────────────────────────────────── */
      .page.guide { flex-direction: column; align-items: stretch; justify-content: flex-start; }
      .guide-h { margin: 0 0 4mm; font-size: 15pt; font-weight: 800; }
      .guide-n { font-weight: 500; color: #888; }
      .guide-grid {
        flex: 1; min-height: 0; display: grid; gap: 6mm;
        grid-template-columns: repeat(${two ? plansPerPage : 1}, 1fr);
        ${two ? '' : `grid-template-rows: repeat(${plansPerPage}, 1fr);`}
      }
      /* Each plan owns a fixed slot and hides its own overflow, so a floor with
         a long legend can never push the next plan off the sheet. */
      .plan { margin: 0; min-height: 0; display: flex; flex-direction: column;
              overflow: hidden; break-inside: avoid; page-break-inside: avoid; }
      .plan figcaption { font-size: 10pt; font-weight: 700; margin-bottom: 1.5mm; }
      .plan-body { flex: 1; min-height: 0; display: flex; gap: 4mm;
                   flex-direction: ${two ? 'column' : 'row'}; }
      .plan.wide { grid-column: 1 / -1; }
      .plan.wide .plan-body { flex-direction: column; }
      .plan-svg { flex: 1; min-width: 0; min-height: 0; border: 0.6px solid #ddd;
                  border-radius: 2mm; overflow: hidden; }
      .plan-svg svg { width: 100%; height: 100%; display: block; }
      .plan-legend {
        flex: none; margin: 0; padding: 0; list-style: none;
        font-size: 7.5pt; line-height: 1.5; overflow: hidden;
        ${two ? 'max-height: 34mm;' : 'width: 58mm;'}
      }
      .plan.wide .plan-legend { width: auto; max-height: 30mm; column-count: 3; column-gap: 6mm; }
      .plan.wide .lg-where { display: inline; }
      .plan-legend li { margin-bottom: 1mm; break-inside: avoid; }
      /* Colour and weight, not a filled chip: a CSS background is dropped when
         "background graphics" is off — the default in most print dialogs — and
         the code has to stay readable on that sheet. (The pills on the plan
         itself are safe either way: SVG fills are content, not backgrounds.) */
      .lg-code { font-weight: 700; letter-spacing: 0.06em; color: #c2185b; }
      /* On its own line where there is a column to hold it: "where it is posted"
         is the sentence somebody reads while looking for the spot. */
      .lg-where { color: #777; ${two ? '' : 'display: block;'} }

      .bar { padding: 8px 0 12px; font: 13px/1.4 -apple-system, Segoe UI, Roboto, sans-serif; }
      .bar button { font: inherit; padding: 5px 14px; }
      /* Removed from the flow entirely when printing, not merely hidden: a
         hidden box with margins can still shift where the first page starts. */
      @media print {
        .bar { display: none !important; }
        html, body { margin: 0 !important; padding: 0 !important; }
      }
    </style>
    <div class="bar">
      <!-- No onclick attribute: this document inherits the site's CSP, which
           refuses inline handlers. The page that writes it wires the button. -->
      <button type="button" data-print>Print</button>
      &nbsp;${signs.length} sign(s) · ${guideChunks.length} guide page(s) + ${chunks.length} sign page(s) · ${esc(prettifySlug(slug))} · ${two ? 'two per page' : 'one per page'}
    </div>
    ${guidePages}${cardPages}`;
}
